import { Link } from 'react-router-dom';
import { ProductImage } from '../../ui/ProductImage';
import { CartGifts } from '../cart/CartGifts';
import { useCartActions } from '../../contexts/StoreContext';
import { shopLabels } from '../../data/shopLabels';
import { isSoldOut } from '../../core/rules/stock';
import { euro } from '../../core/format/currency';
import { euroCents } from './checkoutRules';
import { VoucherField } from './VoucherField';
import type { CheckoutControl } from './useCheckout';

/** De samenvatting naast het formulier: regels, cadeau, verzending en totaal. */
export function OrderSummary({
  c
}: {
  c: CheckoutControl;
}) {
  const {
    lines,
    subtotal,
    giftSubtotal,
    shipping,
    total,
    gifts,
    nextGift,
    appliedCode
  } = c.cart;
  const {
    remove
  } = useCartActions();
  const {
    voucher
  } = c;
  return <aside className="lg:col-span-5" aria-labelledby="co-summary" data-id="element-7110" data-mp="2to5pdvi7vdwn">
      <div id="co-summary-box" className="scroll-mt-24 bg-shell p-6 lg:sticky lg:top-28 lg:p-8" data-id="element-7111" data-mp="2tlsyutye7v7z">
        <h2 id="co-summary" className="scroll-mt-24 font-display text-xl text-ink" data-id="element-7112" data-mp="2ykw52hq407hj">
          Je bestelling
        </h2>
        <ul className="mt-5 divide-y divide-line border-y border-line" data-id="element-7113" data-mp="ot2epnepbgws">
          {lines.map(l => {
          const problem = c.lineProblem(l.product.id, isSoldOut(l.product));
          return <li key={l.product.id} className="flex items-center gap-4 py-4" data-id="element-7114" data-mp="ev4poxp0pg34">
                <div className="relative" data-id="element-7115" data-mp="ex7skib9r3p9">
                  {/* Decoratief: de naam staat ernaast. */}
                  <ProductImage src={l.product.image} alt="" className="h-16 w-14 shrink-0 p-1.5" data-id="element-7116" data-mp="17jk3xi0brude" />
                  <span aria-hidden="true" className="absolute -right-2 -top-2 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-ink px-1 text-[11px] text-shell" data-id="element-7117" data-mp="12styy5vt9tj8">
                    {l.qty}
                  </span>
                </div>
                <div className="flex-1" data-id="element-7118" data-mp="1qio9uhwkl80u">
                  <p className="text-[15px] leading-snug text-ink" data-id="element-7119" data-mp="1qgtoj2hdzdw8">
                    <span className="sr-only" data-id="element-7120" data-mp="3kq07txcvj9ex">{shopLabels.lineQty(l.qty)} </span>
                    {l.product.name}
                  </p>
                  <p className="text-[13px] text-muted" data-id="element-7121" data-mp="3or29eku9jxk2">{l.subscribe ? 'Automatisch bijvullen' : l.product.brand}</p>
                  {problem && <p className="text-[13px] text-clay" data-id="element-7122" data-mp="3fz0aws3r8rcf">
                      {problem} ·{' '}
                      <button type="button" onClick={() => remove(l.product.id)} className="text-ink underline underline-offset-4" data-id="element-7123" data-mp="311rh7rqzbbhj">
                        Weghalen<span className="sr-only" data-id="element-7124" data-mp="362xbhu0af6t6">: {l.product.name}</span>
                      </button>
                    </p>}
                </div>
                <span className="text-[15px] text-ink" data-id="element-7125" data-mp="2wzw5dtbf9fpi">{euroCents(l.product.price * l.qty)}</span>
              </li>;
        })}
        </ul>

        {/* Dezelfde cadeauregels als in de mand: hier kan ze het reisformaat nog wijzigen. */}
        <CartGifts gifts={gifts} compact data-id="element-7126" data-mp="v789k513xujr" />
        {nextGift && <p className="mt-3 text-[13px] text-muted" data-id="element-7127" data-mp="15e14n3syn7wx">
            Nog {euro(nextGift.threshold - giftSubtotal)} tot {nextGift.phrase}.{' '}
            <Link to="/webshop" className="text-ink underline underline-offset-4" data-id="element-7128" data-mp="10cbppd2maa3k">
              Aanvullen
            </Link>
          </p>}

        <dl className="mt-6 space-y-2 text-[14px] text-muted" data-id="element-7129" data-mp="19ds21qczsmaf">
          <div className="flex justify-between" data-id="element-7130" data-mp="1o3r0lu8glzqf">
            <dt data-id="element-7131" data-mp="1o2fxlwmu8w0d">Subtotaal</dt>
            <dd className="text-ink" data-id="element-7132" data-mp="1o5k6367c6dwu">{euroCents(subtotal)}</dd>
          </div>
          <div className="flex justify-between" data-id="element-7133" data-mp="1j44nrlwjmli5">
            <dt data-id="element-7134" data-mp="1x4xhi4f0tck7">Verzending</dt>
            <dd className={shipping === 0 ? 'text-clay' : 'text-ink'} data-id="element-7135" data-mp="1x349olso8mam">{shipping === 0 ? 'Gratis' : euroCents(shipping)}</dd>
          </div>
          {appliedCode && <div className="flex justify-between" data-id="element-7136" data-mp="2puuqehcw8kz">
              <dt data-id="element-7137" data-mp="2qo4zpfixg7v">Code</dt>
              <dd className="text-ink" data-id="element-7138" data-mp="3uu6w4ypnubax">{appliedCode.code}</dd>
            </div>}
          {voucher.cents > 0 && <>
              <div className="flex justify-between" data-id="element-7139" data-mp="4wyb4ufz0id">
                <dt data-id="element-7140" data-mp="551esuvcnv8l">Totaal</dt>
                <dd className="text-ink" data-id="element-7141" data-mp="549s69jqd7ug">{euroCents(total)}</dd>
              </div>
              <div className="flex justify-between" data-id="element-7142" data-mp="96wgw1l3ucnf">
                <dt data-id="element-7143" data-mp="97fvp9fgkkx8">Cadeaubon ···{voucher.last4}</dt>
                <dd className="text-clay" data-id="element-7144" data-mp="e69ar6ae8jzi">−{euroCents(voucher.cents / 100)}</dd>
              </div>
            </>}
        </dl>
        <div className="mt-4 flex items-baseline justify-between border-t border-line pt-4" data-id="element-7145" data-mp="3dmfrf3rn8xpm">
          <span className="text-ink" data-id="element-7146" data-mp="3hn9b1ky1zqcp">
            {voucher.cents > 0 ? 'Te betalen' : 'Totaal'} <span className="text-[13px] text-muted" data-id="element-7147" data-mp="3mkhwpvieucip">incl. btw</span>
          </span>
          <span className="font-display text-3xl text-ink" data-id="element-7148" data-mp="3rcj699o71p51">{euroCents(voucher.payableCents / 100)}</span>
        </div>
        {voucher.cents > 0 && voucher.payableCents === 0 && <p className="mt-2 text-[13px] text-muted" data-id="element-7149" data-mp="2yvozlvq1xymi">Je cadeaubon dekt alles. Je hoeft niets meer te betalen.</p>}
        <VoucherField v={voucher} data-id="element-7150" data-mp="2rcpgzsaf0b47" />
      </div>
    </aside>;
}